// Relative, not the `@/` alias: this module is imported by `vite.config.ts`, which Node loads
// before any of Vite's own resolution exists. `profiles.ts` is relative for the same reason,
// and so are the two catalogue leaves below — they import nothing at run time themselves
// (the pt-BR sibling's only import is a type, which is erased), so pulling them in here
// costs the config graph nothing.

import type { Locale } from '../i18n/locale.ts'
import { brand as brandEn } from '../i18n/messages/en/brand.ts'
import { brand as brandPtBR } from '../i18n/messages/pt-BR/brand.ts'
import type { BrandId } from '../lib/branding.ts'
import { derivedFrom, toOklch } from '../lib/oklch.ts'
import type { BrandProfile } from './profiles.ts'

/**
 * The document's identity, as markup.
 *
 * ── What this is for ──
 *
 * Somebody pastes a link to their deployment into Slack, Teams or a mail client. What comes
 * back is not rendered by the app: it is fetched by a crawler that runs no JavaScript, keeps
 * no cookies and reads the `<head>` and nothing else. Everything the product knows about
 * itself has to be *in the served bytes*, or the preview falls back to the domain name and a
 * grey box — which is what Neoenergia's deployment showed, under the name "Ralph Chat",
 * because the shipped `index.html` carried the reference brand and `brand/active.ts` corrected
 * it from JavaScript that no crawler was ever going to run.
 *
 * So this module states, for one profile: the title, the description, the icons, the colour
 * of the browser chrome, and the full Open Graph and Twitter card. It is a pure function of a
 * profile, which is what lets the same markup be produced in three places that must agree —
 * the dev server, the built `index.html`, and the per-brand variants nginx picks between at
 * container start.
 *
 * ── The origin ──
 *
 * `og:image` has to be an ABSOLUTE URL. Slack, Discord and iMessage resolve a relative one
 * against the page; Teams, LinkedIn, WhatsApp and X do not, and an enterprise deployment is
 * shared in exactly the ones that do not. The origin is not known at build time — one image
 * serves every customer and every hostname — so the build emits a placeholder and nginx
 * substitutes the real scheme and host per request (`sub_filter` in `nginx.conf`). The
 * development server passes `origin: ''`, which leaves ordinary root-relative URLs: nothing
 * unfurls localhost, and a placeholder left visible in the page is its own kind of unpolished.
 *
 * ── On `noindex` ──
 *
 * This is one company's internal assistant on a hostname that happens to resolve publicly. It
 * should not be in a search index, and `robots.txt` is the wrong tool for saying so: a crawler
 * told not to fetch the page never reads the `noindex` in it, and an unfurl bot that honours
 * robots.txt (Slackbot and Twitterbot both do) would stop unfurling. Allowing the fetch and
 * refusing the index is the combination that keeps previews working.
 */

/** What the build writes where the deployment's own origin belongs. See `nginx.conf`. */
export const ORIGIN_PLACEHOLDER = '__RALPH_ORIGIN__'

/**
 * A profile's prose, in one language.
 *
 * The words themselves live in the `brand` message namespace with every other string in the
 * product, so `Messages = typeof en` covers them and the compiler proves the Portuguese
 * exists (PRD-27 LOC-011, LOC-050). This is the one place outside React that reads a
 * catalogue, because a crawler never runs React and the `<head>` is written before any of it
 * mounts.
 */
const CATALOGUES: Record<Locale, typeof brandEn> = { en: brandEn, 'pt-BR': brandPtBR }

export const brandCopy = (locale: Locale, id: BrandId): (typeof brandEn)['profiles'][BrandId] =>
  CATALOGUES[locale].profiles[id]

/**
 * `og:locale` wants a POSIX-ish tag with an underscore, not a BCP-47 one with a hyphen.
 * Facebook, Slack and LinkedIn all read `pt-BR` as absent and fall back to `en_US`, which is
 * the exact failure this exists to stop.
 */
const OG_LOCALE: Record<Locale, string> = { en: 'en_US', 'pt-BR': 'pt_BR' }

/**
 * The social card and the touch icon, by convention rather than configuration.
 *
 * `favicon` is stated per profile because its extension differs — a mark that ships as paths
 * declares `.svg`, one that ships as pixels declares `.png`. These two are always PNG at a
 * fixed size, generated for every profile by `scripts/og-cards.ts`, so naming them in the
 * profile would only create the possibility of naming a file that does not exist.
 */
export const socialImagePath = (id: BrandId): string => `/brands/og-${id}.png`
export const touchIconPath = (id: BrandId): string => `/brands/touch-${id}.png`

/** The generated card's dimensions. Stated to the crawler so it lays out before fetching. */
export const SOCIAL_IMAGE = { width: 1200, height: 630 } as const

/** The apple-touch-icon's, which iOS wants at 180×180. */
export const TOUCH_ICON_SIZE = 180

/**
 * The two desk colours, for `<meta name="theme-color">`.
 *
 * The light desk IS the surface seed — `tokens.css` derives it as `oklch(from … l c h)`,
 * which is the identity. The dark one is the single derivation this file needs a colour space
 * for, and it is transcribed from the same rule that paints the pre-paint background in
 * `index.html`; the two move together or the browser chrome disagrees with the page under it.
 */
export const deskColours = (profile: BrandProfile): { light: string; dark: string } => ({
  light: profile.seeds.surface,
  dark: derivedFrom(toOklch(profile.seeds.surface), 0.156, 0.56)
})

/** `&`, `<`, `>` and `"` — everything that can leave a double-quoted attribute value. */
const attr = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const meta = (kind: 'name' | 'property', key: string, content: string): string =>
  `<meta ${kind}="${key}" content="${attr(content)}" />`

/**
 * The `<head>` identity block for one profile.
 *
 * Returned as a string rather than as tags for a plugin to place, because the same text has to
 * be written into `index.html` at build time AND spliced into the per-brand variants
 * afterwards, and two producers of the same markup is one more thing to keep in step.
 */
export const brandHeadHtml = (
  profile: BrandProfile,
  { origin, locale = profile.defaultLocale }: { origin: string; locale?: Locale }
): string => {
  const url = `${origin}/`
  const image = `${origin}${socialImagePath(profile.id)}`
  const desk = deskColours(profile)
  // The document a crawler is handed comes up in the language the deployment comes up in,
  // because a crawler is the one reader who cannot switch (D-12). The caller passes a locale
  // when it is emitting the per-locale variants; the build-time document takes the brand's
  // own default.
  const copy = brandCopy(locale, profile.id)

  return [
    `<title>${attr(profile.productName)}</title>`,
    meta('name', 'description', copy.description),

    // The name a browser uses for an installed shortcut, and the one iOS puts under a home
    // screen icon. Both fall back to <title>, which is the same string today — but <title>
    // is also what a deployment's operator is most likely to want longer one day.
    meta('name', 'application-name', profile.productName),
    meta('name', 'apple-mobile-web-app-title', profile.productName),

    // The icon a browser draws. `type` moves with `href` — a PNG announced as `image/svg+xml`
    // is believed by Safari, which then draws nothing at all.
    `<link rel="icon" href="${attr(profile.favicon)}" type="${profile.favicon.endsWith('.svg') ? 'image/svg+xml' : 'image/png'}" />`,
    `<link rel="apple-touch-icon" sizes="${TOUCH_ICON_SIZE}x${TOUCH_ICON_SIZE}" href="${attr(touchIconPath(profile.id))}" />`,

    // Every client-side route serves this same document, so the canonical URL is the root.
    // Deliberately NOT built from the request path: nginx would have to interpolate a raw,
    // un-decoded URI into an HTML attribute, and there is nothing here worth that.
    `<link rel="canonical" href="${attr(url)}" />`,

    meta('property', 'og:type', 'website'),
    // No `og:site_name`, deliberately. It names the publisher a page belongs to, and here the
    // publisher and the page are one thing: set to the product name it prints the same words
    // twice in Slack's layout, once small and grey and once bold. Left out, that line falls
    // back to the hostname — which is the piece of information the reader does not already
    // have. `organisation` is not a substitute: on the reference profile it is a prose
    // placeholder ("your organization"), written to sit inside a sentence.
    meta('property', 'og:title', profile.productName),
    meta('property', 'og:description', copy.description),
    meta('property', 'og:url', url),
    meta('property', 'og:locale', OG_LOCALE[locale]),
    meta('property', 'og:image', image),
    meta('property', 'og:image:type', 'image/png'),
    meta('property', 'og:image:width', String(SOCIAL_IMAGE.width)),
    meta('property', 'og:image:height', String(SOCIAL_IMAGE.height)),
    // Describes the CARD, which is a different thing from the page: it carries the mark, the
    // product name and the tagline (`scripts/og-cards.ts`). A crawler that shows alternative
    // text instead of the image should read what the image says.
    meta('property', 'og:image:alt', `${profile.productName} — ${copy.tagline}`),

    // X reads `twitter:*` first and Open Graph second, and the two differ in one thing that
    // matters: without `summary_large_image` the card is a 120px thumbnail beside the text,
    // which is not what a 1200×630 image was drawn for.
    meta('name', 'twitter:card', 'summary_large_image'),
    meta('name', 'twitter:title', profile.productName),
    meta('name', 'twitter:description', copy.description),
    meta('name', 'twitter:image', image),
    meta('name', 'twitter:image:alt', `${profile.productName} — ${copy.tagline}`),

    // The browser's own chrome — the address bar on Android, the status area on iOS — painted
    // the same colour as the desk under it, per theme.
    `<meta name="theme-color" media="(prefers-color-scheme: light)" content="${attr(desk.light)}" />`,
    `<meta name="theme-color" media="(prefers-color-scheme: dark)" content="${attr(desk.dark)}" />`,

    meta('name', 'robots', 'noindex, nofollow')
  ].join('\n    ')
}
